import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";
import { useAuth } from "@/hooks/useAuth";

export interface EnvironmentWallet {
  id: string;
  user_id: string;
  type: string;
  currency: string;
  balance: number;
  locked_balance: number;
  held_balance: number;
  frozen_balance: number;
  /** Computed: balance - held_balance - locked_balance - frozen_balance (never negative) */
  available_balance: number;
  frozen: boolean;
  reference_account_id: string | null;
  created_at: string;
  updated_at: string;
}

/** Compute spendable balance from raw wallet row (held + locked + frozen are all unavailable). */
export function computeAvailableBalance(w: any): number {
  return Math.max(
    0,
    Number(w?.balance || 0) -
      Number(w?.held_balance || 0) -
      Number(w?.locked_balance || 0) -
      Number(w?.frozen_balance || 0)
  );
}

/**
 * Returns wallets from `sandbox_wallets` when platform is in sandbox mode,
 * or from `wallets` when in live mode. Drop-in replacement for direct wallet queries.
 *
 * Stability:
 * - Gated on auth readiness so it never fires during session restore.
 * - Keeps previous data while refetching (no flash to empty).
 * - Retries transient failures and refetches on focus/reconnect.
 */
export function useEnvironmentWallets(userId: string | undefined, filters?: { type?: string; currency?: string }) {
  const { isSandbox, loading: settingsLoading } = usePlatformSettings();
  const { loading: authLoading, session } = useAuth();
  const tableName = isSandbox ? "sandbox_wallets" : "wallets";

  const { data, isLoading, refetch, error } = useQuery({
    queryKey: ["environment_wallets", tableName, userId, filters?.type, filters?.currency],
    queryFn: async () => {
      if (!userId) return [];
      let query = supabase
        .from(tableName as any)
        .select("*")
        .eq("user_id", userId);

      if (filters?.type) query = query.eq("type", filters.type);
      if (filters?.currency) query = query.eq("currency", filters.currency);

      const { data, error } = await query.order("currency");
      if (error) throw error;
      return ((data as any[]) || []).map((w) => ({
        ...w,
        held_balance: Number(w.held_balance || 0),
        frozen_balance: Number(w.frozen_balance || 0),
        available_balance: computeAvailableBalance(w),
      })) as EnvironmentWallet[];
    },
    enabled: !!userId && !settingsLoading && !authLoading && !!session,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  return {
    wallets: data || [],
    isLoading: (isLoading || settingsLoading || authLoading) && !data,
    refetch,
    error,
    isSandbox,
    tableName,
  };
}

/**
 * Helper to get the correct wallet table name based on platform environment.
 * Useful in edge functions and mutation logic.
 */
export function getWalletTableName(isSandbox: boolean): string {
  return isSandbox ? "sandbox_wallets" : "wallets";
}

/**
 * Invalidate every cache entry that depends on user wallet balances.
 * Call this after any mutation that may change a wallet balance
 * (transfers, trades, deposits, withdrawals, demo top-ups, etc.)
 * to keep dashboard, wallets list, exchange, and trading panels in sync.
 */
export function useInvalidateWalletBalances() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["environment_wallets"] });
    qc.invalidateQueries({ queryKey: ["user-trading-wallet"] });
    qc.invalidateQueries({ queryKey: ["user-demo-wallet"] });
    qc.invalidateQueries({ queryKey: ["demo-wallets"] });
    qc.invalidateQueries({ queryKey: ["wallet-transferable-assets"] });
  };
}
